import { useState, useMemo, type ReactElement } from "react";
import type { TimingStatus } from "../standalone/types";
import "./YardCalendarView.css";

type ViewMode = "day" | "week";

interface YardDocument {
    _id: string;
    transportNumber?: string | null;
    status?: number | null;
    timingStatus?: TimingStatus | null;
    fraction?: string | null;
    carrierName?: string | null;
    supplierName?: string | null;
    truckLicensePlate?: string | null;
    plannedArrivalTime?: string | null;
    actualArrivalTime?: string | null;
    timeWindowFrom?: string | null;
    timeWindowTo?: string | null;
    [key: string]: unknown;
}

interface YardCalendarViewProps {
    documents: YardDocument[];
    onDocumentClick: (documentId: string) => void;
    onBackToTable: () => void;
}

const STATUS_LABELS: Record<number, string> = {
    1: "Angemeldet",
    2: "Eingecheckt",
    3: "Beladen",
    4: "Ausgecheckt"
};

const STATUS_COLORS: Record<number, string> = {
    1: "#3b82f6", // Blau
    2: "#10b981", // Grün
    3: "#f59e0b", // Orange
    4: "#6b7280"  // Grau
};

function formatDate(date: Date): string {
    return date.toLocaleDateString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    });
}

function formatTime(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    try {
        const date = new Date(dateStr);
        return date.toLocaleTimeString("de-DE", {
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch {
        return "—";
    }
}

function getWeekDays(baseDate: Date): Date[] {
    const days: Date[] = [];
    const monday = new Date(baseDate);
    const dayOfWeek = monday.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Montag als erster Tag
    monday.setDate(monday.getDate() + diff);

    // Montag bis Samstag (6 Tage)
    for (let i = 0; i < 6; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        days.push(day);
    }

    return days;
}

function isSameDay(date1: Date, date2: Date): boolean {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
}

function getDocumentsForDay(documents: YardDocument[], targetDate: Date): YardDocument[] {
    return documents.filter((doc) => {
        // Priorität: timeWindowFrom > plannedArrivalTime > actualArrivalTime
        const arrivalTime = doc.timeWindowFrom || doc.plannedArrivalTime || doc.actualArrivalTime;
        if (!arrivalTime) return false;

        try {
            const docDate = new Date(arrivalTime as string);
            // Check if date is valid
            if (isNaN(docDate.getTime())) {
                console.warn(`Invalid date for document ${doc._id}: ${arrivalTime}`);
                return false;
            }
            return isSameDay(docDate, targetDate);
        } catch {
            return false;
        }
    });
}

export function YardCalendarView({
    documents,
    onDocumentClick,
    onBackToTable
}: YardCalendarViewProps): ReactElement {
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [viewMode, setViewMode] = useState<ViewMode>("week");

    const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

    const handlePrevious = () => {
        const newDate = new Date(currentDate);
        if (viewMode === "day") {
            newDate.setDate(newDate.getDate() - 1);
        } else {
            newDate.setDate(newDate.getDate() - 7);
        }
        setCurrentDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(currentDate);
        if (viewMode === "day") {
            newDate.setDate(newDate.getDate() + 1);
        } else {
            newDate.setDate(newDate.getDate() + 7);
        }
        setCurrentDate(newDate);
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const renderDayColumn = (date: Date) => {
        const dayDocuments = getDocumentsForDay(documents, date);
        const isToday = isSameDay(date, new Date());

        return (
            <div key={date.toISOString()} className={`calendar-day-column ${isToday ? "today" : ""}`}>
                <div className="calendar-day-header">
                    <div className="calendar-day-name">
                        {date.toLocaleDateString("de-DE", { weekday: "short" })}
                    </div>
                    <div className="calendar-day-date">
                        {date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                    </div>
                    <div className="calendar-day-count">{dayDocuments.length} Fahrten</div>
                </div>

                <div className="calendar-day-content">
                    {dayDocuments.length === 0 ? (
                        <div className="calendar-no-documents">Keine Fahrten</div>
                    ) : (
                        dayDocuments.map((doc) => {
                            const status = typeof doc.status === "number" ? doc.status : 0;
                            const statusLabel = STATUS_LABELS[status] || "Unbekannt";
                            const statusColor = STATUS_COLORS[status] || "#9ca3af";

                            return (
                                <div
                                    key={doc._id}
                                    className="calendar-document-card"
                                    onClick={() => onDocumentClick(doc._id)}
                                    style={{ borderLeftColor: statusColor }}
                                >
                                    <div className="calendar-card-time">
                                        {doc.timeWindowFrom && doc.timeWindowTo
                                            ? `${formatTime(doc.timeWindowFrom)} - ${formatTime(doc.timeWindowTo)}`
                                            : formatTime(doc.timeWindowFrom || doc.plannedArrivalTime || doc.actualArrivalTime)}
                                    </div>
                                    <div className="calendar-card-transport">
                                        {doc.transportNumber || "—"}
                                    </div>
                                    <div className="calendar-card-status" style={{ color: statusColor }}>
                                        {statusLabel}
                                    </div>
                                    {doc.carrierName && (
                                        <div className="calendar-card-carrier">{doc.carrierName}</div>
                                    )}
                                    {doc.truckLicensePlate && (
                                        <div className="calendar-card-truck">{doc.truckLicensePlate}</div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="yard-calendar-view">
            {/* Header mit Navigation */}
            <div className="calendar-header">
                <div className="calendar-header-left">
                    <button
                        type="button"
                        className="calendar-back-button"
                        onClick={onBackToTable}
                    >
                        ← Zurück zur Tabelle
                    </button>
                </div>

                <div className="calendar-header-center">
                    <button
                        type="button"
                        className="calendar-nav-button"
                        onClick={handlePrevious}
                    >
                        ‹
                    </button>

                    <button
                        type="button"
                        className="calendar-today-button"
                        onClick={handleToday}
                    >
                        Heute
                    </button>

                    <div className="calendar-current-range">
                        {viewMode === "day"
                            ? formatDate(currentDate)
                            : `${formatDate(weekDays[0])} - ${formatDate(weekDays[5])}`}
                    </div>

                    <button
                        type="button"
                        className="calendar-nav-button"
                        onClick={handleNext}
                    >
                        ›
                    </button>
                </div>

                <div className="calendar-header-right">
                    <div className="calendar-view-toggle">
                        <button
                            type="button"
                            className={`calendar-toggle-button ${viewMode === "day" ? "active" : ""}`}
                            onClick={() => setViewMode("day")}
                        >
                            Tag
                        </button>
                        <button
                            type="button"
                            className={`calendar-toggle-button ${viewMode === "week" ? "active" : ""}`}
                            onClick={() => setViewMode("week")}
                        >
                            Woche
                        </button>
                    </div>
                </div>
            </div>

            {/* Kalender-Grid */}
            <div className="calendar-grid">
                {viewMode === "day" ? (
                    renderDayColumn(currentDate)
                ) : (
                    weekDays.map((day) => renderDayColumn(day))
                )}
            </div>
        </div>
    );
}
